<script setup lang="ts">
false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    fr: '/politique-de-confidentialite',
  },
})

definePageMeta({
  name: 'PrivacyPolicyPage',
})

const route = useRoute()
const config = useRuntimeConfig()

useHead({
  title: 'Politique de confidentialité - Glouton',
  meta: [
    { name: 'description', content: 'Consultez la politique de confidentialité de Glouton, les renseignements que nous utilisons et les droits dont vous disposez.' },
    { property: 'og:title', content: 'Politique de confidentialité - Glouton' },
    { property: 'og:description', content: 'Consultez la politique de confidentialité de Glouton, les renseignements que nous utilisons et les droits dont vous disposez.' },
    { property: 'og:url', content: computed(() => `${config.public.domain}${route.path}`) },
    { property: 'og:image', content: 'https://glouton.b-cdn.net/site/images/social.jpg' },
  ],
})
</script>

<template>
  <div id="PrivacyPolicyPage" class="bg-sel">
    <UContainer class="py-6 lg:py-8">
      <article class="max-w-4xl mx-auto bg-white rounded-lg px-5 py-6 sm:px-7 lg:px-10">
        <header class="border-b border-poivre-200 pb-4">
          <h1 class="text-2xl font-extrabold leading-tight text-poivre-800 sm:text-3xl font-title">
            Politique de confidentialité de Glouton
          </h1>
          <p class="mt-2 text-sm text-poivre-500">
            Dernière mise à jour : 18 septembre 2026
          </p>
        </header>

        <div class="mt-6 space-y-8 text-sm leading-6 text-poivre-700">
          <section>
            <h2 class="mb-3 text-xl font-bold text-poivre-800 font-title">
              1. Notre engagement
            </h2>
            <div class="space-y-4">
              <p>
                Glouton est exploité par <strong>Recettes Glouton Inc.</strong> Cette politique concerne notre site Web, nos applications et nos communications.
              </p>
              <p>
                <strong>Nous ne vendons pas vos renseignements personnels.</strong> Nous limitons leur collecte et leur utilisation aux besoins des services que vous utilisez et aux autres fins expliquées ci-dessous. Les usages facultatifs nécessitant votre consentement vous sont proposés séparément.
              </p>
              <p>Pour toute question, demande ou plainte :</p>
              <address class="not-italic">
                <strong>Responsable de la protection des renseignements personnels</strong><br>
                Recettes Glouton Inc.<br>
                <a href="mailto:support@glouton.app" class="text-tomate-600 underline hover:text-tomate-700">support@glouton.app</a><br>
                5455, av. De Gaspé, bureau 710<br>
                Montréal (Québec) H2T 3B3
              </address>
            </div>
          </section>

          <section>
            <h2 class="mb-3 text-xl font-bold text-poivre-800 font-title">
              2. Les renseignements que nous utilisons
            </h2>
            <div class="space-y-4">
              <p>
                Vous pouvez consulter les circulaires sans compte. Certaines analyses et fonctions de sauvegarde ou de synchronisation nécessitent une connexion.
              </p>
              <p>Selon les fonctions utilisées, nous recueillons :</p>

              <div class="overflow-x-auto rounded-lg border border-poivre-200" role="region" aria-label="Renseignements utilisés par Glouton" tabindex="0">
                <table class="min-w-[42rem] w-full border-collapse text-left">
                  <thead class="bg-poivre-50">
                    <tr>
                      <th scope="col" class="w-1/2 border-b border-poivre-200 px-4 py-3 font-semibold text-poivre-800">
                        Renseignements
                      </th>
                      <th scope="col" class="w-1/2 border-b border-poivre-200 px-4 py-3 font-semibold text-poivre-800">
                        Utilité
                      </th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-poivre-200">
                    <tr>
                      <td class="px-4 py-3 align-top">
                        Courriel, nom d’affichage et données de connexion
                      </td>
                      <td class="px-4 py-3 align-top">
                        Gérer et sécuriser votre compte
                      </td>
                    </tr>
                    <tr>
                      <td class="px-4 py-3 align-top">
                        Code postal, région, commerces, préférences, listes et favoris
                      </td>
                      <td class="px-4 py-3 align-top">
                        Présenter les informations pertinentes à votre emplacement et organiser vos achats
                      </td>
                    </tr>
                    <tr>
                      <td class="px-4 py-3 align-top">
                        Recettes et contenus que vous enregistrez ou importez
                      </td>
                      <td class="px-4 py-3 align-top">
                        Conserver, traiter et synchroniser vos recettes
                      </td>
                    </tr>
                    <tr>
                      <td class="px-4 py-3 align-top">
                        Renseignements d’abonnement, de transaction et de facturation
                      </td>
                      <td class="px-4 py-3 align-top">
                        Gérer Extra et vos paiements
                      </td>
                    </tr>
                    <tr>
                      <td class="px-4 py-3 align-top">
                        Préférences de communication, adresse courriel et identifiant de notification
                      </td>
                      <td class="px-4 py-3 align-top">
                        Envoyer les communications choisies
                      </td>
                    </tr>
                    <tr>
                      <td class="px-4 py-3 align-top">
                        Messages envoyés au soutien
                      </td>
                      <td class="px-4 py-3 align-top">
                        Répondre à vos demandes
                      </td>
                    </tr>
                    <tr>
                      <td class="px-4 py-3 align-top">
                        IP, données de navigation, informations sur l’appareil et journaux techniques
                      </td>
                      <td class="px-4 py-3 align-top">
                        Faire fonctionner le service, le sécuriser et mesurer son utilisation
                      </td>
                    </tr>
                    <tr>
                      <td class="px-4 py-3 align-top">
                        Choix de consentement
                      </td>
                      <td class="px-4 py-3 align-top">
                        Respecter vos préférences
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p>
                Nous utilisons votre code postal comme donnée de localisation approximative afin de personnaliser les offres et le contenu qui vous sont présentés. Glouton n’utilise aucune donnée de géolocalisation précise. Lorsque certains outils demandent l’accès à la géolocalisation de votre appareil, cette information est traitée uniquement localement sur votre appareil; elle n’est jamais transmise à Glouton ni enregistrée par Glouton.
              </p>

              <p>
                Ces renseignements proviennent de vous, de votre utilisation du service et, pour les abonnements, des fournisseurs de paiement ou des boutiques d’applications. Les champs nécessaires sont identifiés lors de la collecte.
              </p>
            </div>
          </section>

          <section>
            <h2 class="mb-3 text-xl font-bold text-poivre-800 font-title">
              3. Témoins, pixels et vos choix
            </h2>
            <div class="space-y-4">
              <p>
                Nous utilisons des témoins (« cookies ») et outils similaires pour faire fonctionner Glouton, maintenir votre connexion et mémoriser vos préférences.
              </p>
              <p>
                Sur notre site Web et dans nos applications, nous utilisons aussi des pixels publicitaires pour mesurer nos campagnes et, si applicable, personnaliser ou recibler nos publicités. Ils transmettent aux plateformes publicitaires des renseignements sur votre utilisation de Glouton, comme les pages consultées, les interactions et les identifiants. Ces plateformes peuvent les associer aux renseignements qu’elles possèdent déjà.
              </p>
              <p>
                Les outils de suivi publicitaire ne sont activés qu’avec votre consentement. Vous pouvez accepter ou refuser ce suivi et modifier votre choix dans l’outil de consentement du site Web ou les paramètres de confidentialité de l’application. Votre refus ne vous prive pas des fonctions qui ne dépendent pas de ce suivi. Nous respectons également les autorisations de suivi de votre appareil.
              </p>
              <p>
                Vous pouvez vous désabonner de l’infolettre avec le lien inclus dans les messages et gérer les notifications dans l’application ou votre appareil. Pour retirer un autre consentement, écrivez à <a href="mailto:support@glouton.app" class="text-tomate-600 underline hover:text-tomate-700">support@glouton.app</a>. Nous vous expliquons les conséquences si une fonction en dépend.
              </p>
            </div>
          </section>

          <section>
            <h2 class="mb-3 text-xl font-bold text-poivre-800 font-title">
              4. Fournisseurs et hébergement
            </h2>
            <div class="space-y-4">
              <p>
                Nous faisons appel à des fournisseurs d’hébergement et de stockage, de paiement, d’envoi de courriels et de notifications, de mesure d’audience et de publicité. Ils reçoivent les renseignements nécessaires à leur rôle. Les communications publicitaires sont soumises aux choix décrits plus haut.
              </p>
              <p>
                Nous pouvons utiliser des services d’extraction et des outils d’intelligence artificielle pour extraire, structurer ou analyser le contenu de recettes et de circulaires afin de fournir les fonctionnalités de Glouton. Lors d’une importation de recette, ces outils peuvent traiter le lien, le texte ou l’image que vous fournissez. Nous ne transmettons pas aux fournisseurs d’IA externes votre nom, votre adresse courriel ni l’identifiant de votre compte Glouton. Vos recettes importées ne sont pas ajoutées au catalogue public.
              </p>
              <p>
                <strong>Le serveur de Glouton et les données qui y sont conservées sont hébergés au Canada.</strong> Nos outils d’infolettre et de service à la clientèle sont hébergés dans l’Union européenne et traitent les renseignements nécessaires à ces services.
              </p>
              <p>
                Autant que possible, nous privilégions des fournisseurs canadiens ou dont les services sont hébergés au Canada. D’autres fournisseurs, notamment de paiement, de mesure d’audience ou de publicité, peuvent traiter certains renseignements ailleurs. Les traitements à l’extérieur du Québec sont encadrés par les évaluations et ententes requises et peuvent être soumis aux lois des territoires concernés.
              </p>
              <p>
                Au sein de notre équipe, l’accès est limité aux personnes qui en ont besoin pour le soutien, les opérations, la facturation ou la sécurité. Le partage d’une liste donne accès aux personnes que vous invitez. Nous pouvons également communiquer des renseignements lorsque la loi l’exige ou l’autorise, dans les limites qu’elle prévoit.
              </p>
            </div>
          </section>

          <section>
            <h2 class="mb-3 text-xl font-bold text-poivre-800 font-title">
              5. Protection et conservation
            </h2>
            <div class="space-y-4">
              <p>
                Notre responsable supervise la protection des renseignements et le traitement des demandes et plaintes. Les membres de l’équipe autorisés les utilisent uniquement dans le cadre de leurs fonctions. Nos règles internes encadrent leurs accès, la conservation et la destruction des données.
              </p>
              <p>
                Nous appliquons des mesures de sécurité adaptées aux renseignements traités. En cas d’incident présentant un risque de préjudice sérieux, nous avisons avec diligence les personnes concernées et la Commission d’accès à l’information, conformément à la loi.
              </p>
              <p>
                Les renseignements du compte sont conservés pour vous fournir les services utilisés. Lorsqu’ils ne sont plus nécessaires, ils sont détruits ou anonymisés selon les règles applicables. Certaines pièces doivent être conservées plus longtemps pour respecter une obligation légale, notamment comptable. Les copies de sauvegarde sont supprimées selon leur cycle de conservation.
              </p>
              <p>
                Vous pouvez nous demander les durées applicables à vos renseignements. Fermer un compte ne nous autorise pas à poursuivre les usages facultatifs auxquels vous avez mis fin.
              </p>
            </div>
          </section>

          <section>
            <h2 class="mb-3 text-xl font-bold text-poivre-800 font-title">
              6. Vos droits et vos demandes
            </h2>
            <div class="space-y-4">
              <p>
                Vous pouvez supprimer votre compte dans la section <strong>Profil</strong>, ou en faire la demande à <a href="mailto:support@glouton.app" class="text-tomate-600 underline hover:text-tomate-700"><strong>support@glouton.app</strong></a>. La suppression reste soumise aux obligations de conservation indiquées plus haut.
              </p>
              <p>
                Écrivez à la même adresse pour accéder à vos renseignements, les faire corriger ou retirer un consentement.
              </p>
              <p>
                Dans les conditions prévues par la loi, vous pouvez aussi demander une copie portable des renseignements informatisés recueillis auprès de vous, leur transmission à un destinataire autorisé, la cessation de leur diffusion ou leur désindexation.
              </p>
              <p>
                Nous répondons par écrit aux demandes d’accès ou de rectification dans les <strong>30 jours</strong> suivant leur réception. Un refus est motivé et précise les recours applicables.
              </p>
              <p>
                Pour une plainte, décrivez le problème à la même adresse. Notre responsable l’examine, vous contacte au besoin et vous communique les conclusions et les mesures retenues.
              </p>
            </div>
          </section>

          <section>
            <h2 class="mb-3 text-xl font-bold text-poivre-800 font-title">
              7. Modifications
            </h2>
            <p>
              Les modifications à cette politique seront annoncées par un avis sur notre site ou dans l’application, précisant les changements et leur date d’entrée en vigueur. Elles ne nécessitent pas systématiquement une nouvelle acceptation. Si un changement exige votre consentement en vertu de la loi, nous vous le demanderons avant de l’appliquer aux renseignements concernés.
            </p>
          </section>
        </div>
      </article>
    </UContainer>
  </div>
</template>
